import { validateSourceHeaders, validateAugmentationHeaders, readCSVHeaders } from './fileValidation';

/**
 * Organization-specific date format configurations
 * @param {string} organizationId - Organization ID
 * @returns {Object|null} Date format configuration or null if no validation needed
 */
export const getDateFormatConfig = (organizationId) => {
    if (organizationId === '2') {
        return { format: 'yyyy-mm-dd', regex: /\d{4}-\d{2}-\d{2}/ };
    } else if (organizationId === '8') {
        return { format: 'yyyymmdd', regex: /\d{8}/ };
    }
    return null;
};

/**
 * Extract and validate date from filename
 * @param {string} fileName - File name to validate
 * @param {string} organizationId - Organization ID
 * @param {string} currentMonth - Current month in YYYY-MM format
 * @returns {Object} Validation result with isValid, isCurrentMonth, fileDate, dateStr, and error
 */
export const validateFileDate = (fileName, organizationId, currentMonth) => {
    const config = getDateFormatConfig(organizationId);
    if (!config) return { isValid: true }; // No validation for other orgs

    const dateMatch = fileName.match(config.regex);
    if (!dateMatch) {
        return {
            isValid: false,
            error: `Filename must contain a date in ${config.format} format`
        };
    }

    const dateStr = dateMatch[0];
    let fileDate;
    
    if (config.format === 'yyyy-mm-dd') {
        fileDate = new Date(dateStr);
    } else if (config.format === 'yyyymmdd') {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        fileDate = new Date(`${year}-${month}-${day}`);
    }

    if (isNaN(fileDate.getTime())) {
        return {
            isValid: false,
            error: `Invalid date format in filename: ${dateStr}`
        };
    }

    const fileMonth = fileDate.toISOString().slice(0, 7);
    const isCurrentMonth = fileMonth === currentMonth;

    return {
        isValid: true,
        isCurrentMonth,
        fileDate,
        dateStr
    };
};

/**
 * File type enumeration
 */
export const FileType = {
    SOURCE: 'source',
    AUGMENTATION: 'augmentation'
};

/**
 * Generic file change handler that validates headers and creates pending file data
 * @param {Array} newFiles - Array of newly selected files
 * @param {Object} config - Configuration object
 * @param {string} config.fileType - FileType.SOURCE or FileType.AUGMENTATION
 * @param {string} config.organizationId - Organization ID (for source files)
 * @param {Array} config.existingFiles - Existing files array (for augmentation files)
 * @param {Function} config.setFiles - Function to set files state
 * @param {Function} config.setPendingFileMetadata - Function to set pending file metadata state
 * @param {Function} config.setHeaderError - Function to set header error state
 * @param {string} config.errorMessage - Error message to display on validation failure
 * @returns {Promise<void>}
 */
export const handleFileChange = async (newFiles, config) => {
    const {
        fileType,
        organizationId,
        existingFiles,
        setFiles,
        setPendingFileMetadata,
        setHeaderError,
        errorMessage
    } = config;

    if (newFiles.length > 0) {
        const file = newFiles[0];
        let validation;

        // Validate based on file type
        if (fileType === FileType.SOURCE) {
            validation = await validateSourceHeaders(file, organizationId);
        } else if (fileType === FileType.AUGMENTATION) {
            validation = await validateAugmentationHeaders(file, existingFiles);
        } else {
            throw new Error(`Unknown file type: ${fileType}`);
        }
        
        if (!validation.isValid) {
            setHeaderError(errorMessage);
            // Reset everything on validation failure
            setFiles([]);
            setPendingFileMetadata(null);
            return;
        } else {
            setHeaderError(null);
            setFiles(newFiles);
            
            // Create pending file data with headers
            const fileHeaders = await readCSVHeaders(file);
            const pendingFileMetadata = {
                file_name: validation.matchingFile ? validation.matchingFile.file_name : file.name,
                size: file.size || 0,
                last_modified: file.lastModified ? new Date(file.lastModified).toISOString() : new Date().toISOString(),
                isPending: true,
                header: fileHeaders.join(',')
            };
            setPendingFileMetadata(pendingFileMetadata);
        }
    } else {
        setHeaderError(null);
        setFiles([]);
        setPendingFileMetadata(null);
    }
};